import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import { display, width } from 'styled-system';
import type { DisplayProps, WidthProps } from 'styled-system';

import PrismicResponsiveImage from 'common/components/PrismicResponsiveImage';
import type { PrismicImage } from 'common/components/PrismicResponsiveImage';
import { aspectRatio as aspectRatioUtil } from 'common/theme/system-utilities';
import type { AspectRatioProps } from 'common/theme/system-utilities';
import {
  BIG_VARIANT_ASPECT_RATIO,
  HALF_SIZE_VARIANT_IMAGE_WIDTH,
  SLIM_VARIANT_ASPECT_RATIO,
} from '../../constants';
import { ImageContainerType } from '../../enums';
import type { TeaserProps } from '../../types';

interface ResponsiveImageProps {
  type: ImageContainerType;
  image: PrismicImage;
  isFullWidth: boolean;
  ctaButtonUrl?: TeaserProps['ctaButtonUrl'];
  onClick?: () => void;
}

const Container = styled.div<WidthProps & DisplayProps & AspectRatioProps>`
  ${width};
  ${display};
  ${aspectRatioUtil};
`;

const containerStyles: Partial<ComponentProps<typeof Container>> = {
  width: '100%',
  display: 'block',
};

const StyledPrismicImage = styled(PrismicResponsiveImage)<
  WidthProps & DisplayProps & AspectRatioProps
>`
  ${width};
  ${display};
  ${aspectRatioUtil};
`;

const styledPrismicImageStyles: Partial<ComponentProps<typeof StyledPrismicImage>> = {
  width: '100%',
  display: 'block',
};

const ResponsiveImage = ({
  type,
  image,
  isFullWidth,
  ctaButtonUrl,
  onClick,
}: ResponsiveImageProps) => {
  const aspectRatio =
    type === ImageContainerType.Big ? BIG_VARIANT_ASPECT_RATIO : SLIM_VARIANT_ASPECT_RATIO;
  const handleClick = () => {
    if (ctaButtonUrl?.url && onClick) onClick();
  };

  return (
    <Container
      {...containerStyles}
      aspectRatio={aspectRatio}
      width={isFullWidth ? '100%' : ['100%', null, HALF_SIZE_VARIANT_IMAGE_WIDTH]}
      {...(ctaButtonUrl?.url
        ? {
            as: 'a',
            href: ctaButtonUrl.url,
            onClick: handleClick,
          }
        : {})}
    >
      <StyledPrismicImage
        {...styledPrismicImageStyles}
        highPriority
        image={image}
        aspectRatio={aspectRatio}
      />
    </Container>
  );
};

export default ResponsiveImage;
